import { ChevronDown } from 'lucide-react';

import { buttonVariants } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { cn } from '../utils';

export type SupplierCategoryOption = {
  label: string;
  value: string;
  inactive?: boolean;
};

type SupplierCategoriesMultiSelectProps = {
  options: SupplierCategoryOption[];
  selectedValues: string[];
  placeholder: string;
  allSelectedLabel?: string;
  emptyStateLabel?: string;
  disabled?: boolean;
  ariaLabel: string;
  preventEmptySelection?: boolean;
  showSelectAllToggle?: boolean;
  selectAllLabel?: string;
  clearAllLabel?: string;
  onChange: (nextValues: string[]) => void;
};

const optionsContainerClassName =
  'min-h-0 flex-1 space-y-1 overflow-y-auto overscroll-contain';

function buildTriggerLabel(
  options: SupplierCategoryOption[],
  selectedValues: string[],
  placeholder: string,
  allSelectedLabel?: string,
) {
  if (selectedValues.length === 0) {
    return placeholder;
  }

  const selectedOptions = options.filter((option) => selectedValues.includes(option.value));
  if (allSelectedLabel && options.length > 0 && selectedOptions.length === options.length) {
    return allSelectedLabel;
  }

  if (selectedOptions.length === 1) {
    return selectedOptions[0].label;
  }

  return `${selectedOptions.length} categorías`;
}

export function SupplierCategoriesMultiSelect({
  options,
  selectedValues,
  placeholder,
  allSelectedLabel,
  emptyStateLabel = 'No hay categorías disponibles.',
  disabled = false,
  ariaLabel,
  preventEmptySelection = false,
  showSelectAllToggle = false,
  selectAllLabel = 'Seleccionar todo',
  clearAllLabel = 'Deseleccionar todo',
  onChange,
}: SupplierCategoriesMultiSelectProps) {
  const selectedValuesSet = new Set(selectedValues);
  const selectableOptions = options.filter((option) => !option.inactive);
  const selectableValues = selectableOptions.map((option) => option.value);
  const hasAllSelectableValuesSelected =
    selectableValues.length > 0
    && selectableValues.every((value) => selectedValuesSet.has(value));

  const toggleValue = (value: string) => {
    const nextSelection = new Set(selectedValues);
    if (nextSelection.has(value)) {
      if (preventEmptySelection && nextSelection.size === 1) {
        return;
      }
      nextSelection.delete(value);
    } else {
      nextSelection.add(value);
    }

    const orderedValues = options
      .filter((option) => nextSelection.has(option.value))
      .map((option) => option.value);
    onChange(orderedValues);
  };

  const handleToggleAll = () => {
    if (hasAllSelectableValuesSelected) {
      onChange([]);
      return;
    }

    onChange(selectableValues);
  };

  return (
    <Popover modal>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          aria-label={ariaLabel}
          className={cn(
            buttonVariants({ variant: 'outline' }),
            'w-full justify-between font-normal',
          )}
        >
          <span className="truncate">
            {buildTriggerLabel(options, selectedValues, placeholder, allSelectedLabel)}
          </span>
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 text-gray-500" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        collisionPadding={16}
        className="flex max-h-[min(18rem,var(--radix-popover-content-available-height))] w-72 flex-col overflow-hidden p-2"
      >
        {options.length === 0 ? (
          <p className="px-2 py-3 text-sm text-gray-500">{emptyStateLabel}</p>
        ) : (
          <div className="flex min-h-0 flex-1 flex-col gap-2">
            {showSelectAllToggle ? (
              <button
                type="button"
                onClick={handleToggleAll}
                className="w-full shrink-0 rounded-md px-2 py-2 text-left text-sm font-medium text-sky-700 hover:bg-sky-50"
              >
                {hasAllSelectableValuesSelected ? clearAllLabel : selectAllLabel}
              </button>
            ) : null}
            <div
              data-slot="supplier-categories-options"
              className={optionsContainerClassName}
              onWheelCapture={(event) => event.stopPropagation()}
            >
              {options.map((option) => {
                const isSelected = selectedValuesSet.has(option.value);
                const isSelectionBlocked = Boolean(option.inactive) && !isSelected;

                return (
                  <button
                    type="button"
                    key={option.value}
                    onClick={() => {
                      if (isSelectionBlocked) return;
                      toggleValue(option.value);
                    }}
                    className={`flex w-full items-center gap-3 rounded-md px-2 py-2 text-left text-sm ${
                      isSelectionBlocked ? 'cursor-not-allowed opacity-60' : 'hover:bg-gray-50'
                    }`}
                  >
                    <Checkbox checked={isSelected} disabled={isSelectionBlocked} />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-gray-900">{option.label}</p>
                      {option.inactive ? (
                        <p className="text-xs text-amber-700">Categoría inactiva</p>
                      ) : null}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
